import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
// material
import {
  Button,
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { filter } from 'lodash';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';

//
import { useFormik } from 'formik';
import { ClubListHead, ClubListToolbar, ClubMoreMenu } from '../components/club';
import Label from '../components/Label';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { useTranslation } from '../common/LocalizationProvider';
import { selectGroupsArray, selectGroupsTokenArray } from '../createSelectorUtils';

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_club) =>
        _club.name.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        (_club.email != null
          ? _club.email.toLowerCase().indexOf(query.toLowerCase()) !== -1
          : false) ||
        (_club.phone !== null ? _club.phone.indexOf(query) !== -1 : false) ||
        (_club.siteWeb !== null
          ? _club.siteWeb.toLowerCase().indexOf(query.toLowerCase()) !== -1
          : false)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

function substringToken(token) {
  return token.length > 3 ? `${token.substring(0, 4)}...` : token;
}

export default function Club() {
  const t = useTranslation();

  const TABLE_HEAD = [
    { id: 'name', label: t('fields_Name'), alignRight: false },
    { id: 'email', label: t('fields_Email'), alignRight: false },
    { id: 'phone', label: t('fields_Phone'), alignRight: false },
    { id: 'siteWeb', label: t('fields_Website'), alignRight: false },
    { id: 'token', label: t('fields_Token'), alignRight: false },
    { id: '' }
  ];

  const formik = useFormik({
    initialValues: {
      club: null
    }
  });
  // ----------------------------------------------------------------------

  const allGroupsData = useSelector(selectGroupsArray);
  const allGroups = useMemo(
    () =>
      formik.values.club == null
        ? allGroupsData
        : allGroupsData.filter((item) => item.id === formik.values.club.id),
    [allGroupsData, formik.values.club]
  );

  const groupsToken = useSelector(selectGroupsTokenArray);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - allGroups.length) : 0;

  const filteredClubs = applySortFilter(allGroups, getComparator(order, orderBy), filterName);

  const isClubNotFound = filteredClubs.length === 0;

  return (
    <Page title={t('CL_Title')}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('clubsList')}
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="add"
            startIcon={<Icon icon={plusFill} />}
          >
            {t('CL_NewClub')}
          </Button>
        </Stack>

        <Card>
          <ClubListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 300 }}>
              <Table>
                <ClubListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={allGroups.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredClubs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { id, name, email, phone, siteWeb, token } = {
                        ...row,
                        token: groupsToken
                          .filter((item) => item.groupId === row.id)
                          .map((item) => substringToken(item.token))
                      };
                      return (
                        <TableRow hover key={id}>
                          <TableCell component="th" scope="row">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                                {name}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="left">{email}</TableCell>
                          <TableCell align="left">{phone}</TableCell>
                          <TableCell align="left">
                            {siteWeb !== undefined && siteWeb !== null && (
                              <Label variant="ghost" color="info">
                                {siteWeb}
                              </Label>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            <Label variant="ghost" color="error">
                              {token}
                            </Label>
                          </TableCell>
                          <TableCell align="right">
                            <ClubMoreMenu id={id} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>
                {isClubNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 50, 100, 250, 500]}
            component="div"
            count={filteredClubs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
